import React, { useEffect } from "react";
import { FaWhatsapp } from "react-icons/fa";

import {
  Card,
  CardContent,
  Avatar,
  CardActions,
  Button,
  Chip,
  Typography,
  Box,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AccountCircle, Event, AttachMoney } from "@material-ui/icons";
import useBDocTranslator from "../hooks/useBDocTranslator";

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    width: "100%",
    maxWidth: 400,
    margin: theme.spacing(1),
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: "100%",
      margin: theme.spacing(0.5),
    },
  },
  cardContent: {
    padding: theme.spacing(2.5),
    paddingBottom: theme.spacing(2),
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  serviceInfo: {
    display: "flex",
    alignItems: "center",
    flex: 1,
  },
  avatar: {
    width: 48,
    height: 48,
    backgroundColor: theme.palette.primary.main,
    marginRight: theme.spacing(1.5),
  },
  serviceTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.3,
  },
  providerName: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    display: "flex",
    alignItems: "center",
  },
  statusChip: {
    fontWeight: 500,
    fontSize: "0.75rem",
    height: 28,
    borderRadius: 14,
  },
  detailsSection: {
    marginTop: theme.spacing(2),
  },
  detailItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  detailIcon: {
    fontSize: 18,
    marginRight: theme.spacing(1),
    color: theme.palette.primary.light,
  },
  detailText: {
    fontSize: "0.875rem",
  },
  priceText: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  divider: {
    margin: theme.spacing(0, -2.5),
    backgroundColor: "rgba(0, 0, 0, 0.08)",
  },
  cardActions: {
    padding: theme.spacing(1.5, 2.5),
    backgroundColor: "rgba(7, 108, 216, 0.02)",
  },
  helpButton: {
    textTransform: "none",
    fontWeight: 500,
    borderRadius: 20,
    padding: theme.spacing(0.75, 2),
    "&:hover": {
      backgroundColor: "rgba(76, 175, 80, 0.1)",
    },
  },
  whatsappIcon: {
    fontSize: 18,
    color: "#25D366",
  },
}));

export default function OrderCard({
  serviceName,
  servicePrice,
  providerName,
  serviceStatus,
  apptDate,
  handleShowDetails,
}) {
  const classes = useStyles();
  const { t, changeDirectLanguage } = useBDocTranslator();

  const chipColor = (status) => {
    let result = "default";

    if (servicePrice === 0) return (result = "success");

    switch (status) {
      case "completed":
        result = "success";
        break;
      case "canceled":
        result = "error";
        break;
      case "generated":
        result = "warning";
        break;
      default:
        result = "default";
    }

    return result;
  };

  const chipStatusText = (status) => {
    let result = "NO-Status";

    if (servicePrice === 0) return (result = "Payed");

    switch (status) {
      case "completed":
        result = "Payed";
        break;
      case "canceled":
        result = "Cancelled";
        break;
      case "generated":
        result = "Awaiting for Payment";
        break;
      default:
        result = "NO-Status";
    }

    return t(result);
  };

  useEffect(() => {
    changeDirectLanguage();
  }, []);

  return (
    <Card className={classes.cardContainer} variant="elevation" elevation={0}>
      <CardContent className={classes.cardContent}>
        {/* Header Section */}
        <Box className={classes.header}>
          <Box className={classes.serviceInfo}>
            <Avatar className={classes.avatar}>
              <AccountCircle />
            </Avatar>
            <Box>
              <Typography variant="h6" className={classes.serviceTitle}>
                {serviceName || "SERVICE NAME"}
              </Typography>
              <Typography className={classes.providerName}>
                <AccountCircle className={classes.detailIcon} />
                {providerName || t("Provider")}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={chipStatusText(serviceStatus)}
            color={chipColor(serviceStatus)}
            className={classes.statusChip}
            size="small"
          />
        </Box>

        {/* Details Section */}
        <Box className={classes.detailsSection}>
          <Box className={classes.detailItem}>
            <Event className={classes.detailIcon} />
            <Typography className={classes.detailText}>
              {t("Scheduled on")} <strong>{apptDate}</strong>
            </Typography>
          </Box>
          <Box className={classes.detailItem}>
            <AttachMoney className={classes.detailIcon} />
            <Typography className={classes.detailText}>
              {t("Total amount")}: <span className={classes.priceText}>${servicePrice}</span>
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <Divider className={classes.divider} />

      <CardActions className={classes.cardActions}>
        <Button
          className={classes.helpButton}
          onClick={handleShowDetails}
          endIcon={<FaWhatsapp className={classes.whatsappIcon} />}
          color="inherit"
          size="small"
        >
          {t("Need Help")}
        </Button>
      </CardActions>
    </Card>
  );
}
